/**
 * TELL ME WHEN — the desktop modal (DESIGN-CHANGELOG 2026-09-03; `design/GT
 * Mine.dc.html:355-418`). AA phase 4.
 *
 * 530px. The game block (header art · name · dev · EARLY ACCESS / UNRELEASED
 * and price chips), then the seven trigger rows in the matrix's order: an
 * 18px box, the row label, a one-line gloss — or, on a row the game cannot
 * carry, dashed, greyed and the REASON. ANY SALE wears THE NOISY ONE when on;
 * the PRICE row carries the `− $N +` floor and its `−40% OFF` tail. Footer:
 * `ALSO PUT IT ON MY BACKLOG` (only when it is not shelved) · CANCEL · SAVE ▸.
 * Edits a draft; only SAVE writes. Root-mounted — every bell opens it.
 */

import { CoverArt } from '../../components/CoverArt.tsx';
import { useArt } from '../../components/useArt.ts';
import { useQd } from '../../store/index.ts';
import { tellMeWhenVM } from '../../store/selectors.ts';
import { Modal } from '../components/Modal.tsx';

const MONO = 'var(--gt-font-mono)';

export default function TellMeWhenModal(): JSX.Element {
  const s = useQd();
  const v = tellMeWhenVM(s);
  const art = useArt(v.id + 'w', v.hdrCands);

  return (
    <Modal
      onClose={() => s.closeWatch()}
      width={530}
      label="Tell me when modal"
      header={{ title: 'TELL ME WHEN', titleClr: 'var(--gt-text)', titleFs: '10px', note: 'ONLY WHAT YOU ARM REACHES YOU' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px', borderBottom: '1px solid var(--gt-line)' }}>
        <div style={{ position: 'relative', width: 132, height: 62, flex: 'none', background: v.fall, overflow: 'hidden', border: '1px solid var(--gt-line)' }}>
          <CoverArt style={{ position: 'absolute', inset: 0 }} fs={11} ini={v.ini} hasCover={art.hasCover} cover={art.cover} />
        </div>
        <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontFamily: 'var(--gt-font-sans)', fontWeight: 600, fontSize: 15, color: 'var(--gt-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {v.n}
          </span>
          <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.07em', color: 'var(--gt-faint)' }}>{v.dev}</span>
          <div style={{ display: 'flex', gap: 6 }}>
            {v.stateChip ? (
              <span style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '.08em', color: 'var(--gt-info)', border: '1px solid var(--gt-info)', padding: '2px 6px' }}>{v.stateChip}</span>
            ) : null}
            <span style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '.08em', color: 'var(--gt-text-4)', border: '1px solid var(--gt-border-2)', padding: '2px 6px' }}>{v.priceChip}</span>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {v.rows.map((r) => (
          <div
            key={r.key}
            onClick={() => (r.na ? undefined : s.wTog(r.k))}
            data-tip={r.na ? r.sub : undefined}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '10px 18px',
              borderBottom: '1px solid var(--gt-line-0)',
              background: r.rowBg,
              cursor: r.cur,
            }}
          >
            <span
              style={{
                width: 18,
                height: 18,
                flex: 'none',
                boxSizing: 'border-box',
                border: '1px ' + r.boxBs + ' ' + r.boxBd,
                background: r.on ? 'var(--acc)' : 'transparent',
                display: 'grid',
                placeItems: 'center',
                fontFamily: MONO,
                fontSize: 10,
                color: r.on ? 'var(--gt-ink)' : 'var(--gt-ghost)',
              }}
            >
              {r.on ? '■' : r.na ? '–' : ''}
            </span>
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '.06em', color: r.labClr }}>{r.l}</span>
                {r.hasWarn ? (
                  <span style={{ fontFamily: MONO, fontSize: 7.5, letterSpacing: '.09em', color: 'var(--acc2)', background: 'var(--acc2Soft)', border: '1px solid var(--acc2)', padding: '2px 5px' }}>
                    THE NOISY ONE
                  </span>
                ) : null}
              </div>
              <span style={{ fontFamily: MONO, fontSize: 8.5, letterSpacing: '.05em', color: r.subClr }}>{r.sub}</span>
            </div>
            {r.isPrice && !r.na ? (
              <div onClick={(ev) => ev.stopPropagation()} style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 'none' }}>
                <div style={{ display: 'flex', border: '1px solid var(--gt-border-2)' }}>
                  <span onClick={() => s.wCap(-1)} style={{ width: 24, height: 24, display: 'grid', placeItems: 'center', fontFamily: MONO, fontSize: 11, color: 'var(--gt-text-4)', cursor: 'pointer' }}>−</span>
                  <span style={{ minWidth: 34, height: 24, display: 'grid', placeItems: 'center', fontFamily: MONO, fontSize: 10, color: 'var(--gt-text)', borderLeft: '1px solid var(--gt-border-2)', borderRight: '1px solid var(--gt-border-2)', padding: '0 4px' }}>
                    {v.capTxt}
                  </span>
                  <span onClick={() => s.wCap(1)} style={{ width: 24, height: 24, display: 'grid', placeItems: 'center', fontFamily: MONO, fontSize: 11, color: 'var(--gt-text-4)', cursor: 'pointer' }}>+</span>
                </div>
                {v.hasCapPct ? <span style={{ fontFamily: MONO, fontSize: 8.5, letterSpacing: '.06em', color: 'var(--gt-faint)', whiteSpace: 'nowrap' }}>{v.capPct} OFF</span> : null}
              </div>
            ) : null}
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '13px 18px', borderTop: '1px solid var(--gt-line)', background: 'var(--gt-raised)' }}>
        {v.alsoShown ? (
          <span onClick={() => s.wAlsoTog()} style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: MONO, fontSize: 9, letterSpacing: '.07em', color: v.also ? 'var(--acc)' : 'var(--gt-text-5)', cursor: 'pointer' }}>
            <span style={{ width: 14, height: 14, boxSizing: 'border-box', border: '1px solid ' + (v.also ? 'var(--acc)' : 'var(--gt-border-2)'), background: v.also ? 'var(--acc)' : 'transparent', display: 'grid', placeItems: 'center', fontSize: 9, color: 'var(--gt-ink)' }}>
              {v.also ? '✓' : ''}
            </span>
            ALSO PUT IT ON MY BACKLOG
          </span>
        ) : null}
        <div style={{ flex: 1 }} />
        <span onClick={() => s.closeWatch()} style={{ fontFamily: MONO, fontSize: '9.5px', letterSpacing: '.07em', color: 'var(--gt-text-5)', border: '1px solid var(--gt-border-2)', padding: '9px 12px', cursor: 'pointer' }}>
          CANCEL
        </span>
        <span
          onClick={() => void s.wSave()}
          style={{ fontFamily: 'var(--gt-font-pixel)', fontSize: 8, color: 'var(--gt-ink)', background: 'var(--acc)', border: '1px solid var(--acc)', padding: '9px 14px', cursor: 'pointer' }}
        >
          <span style={{ display: 'inline-block', transform: 'translateY(0.13em)' }}>{v.saveL}</span>
        </span>
      </div>
    </Modal>
  );
}
